import { deriveAccentScale } from "@aio/platform-tokens";

export interface BrandPalette {
  /**
   * Primary accent as a raw `H S% L%` triplet (4.5+ contrast against white).
   * Designed to be assigned to a CSS custom property that the Tailwind preset
   * wraps with `hsl(var(--accent) / <alpha-value>)`. NEVER pre-wrap with
   * `hsl(...)` here — the consumer wrap would yield invalid CSS.
   */
  accent: string;
  /** Foreground for accent backgrounds, raw `H S% L%` triplet (see `accent`). */
  accentFg: string;
  /**
   * Darker variant of the accent for use as text on accent-tinted backgrounds
   * (e.g. `bg-accent/10` active states). Snapped to L=38 so it clears
   * 4.5:1 contrast against the alpha-composited 10%-tint background.
   * Raw `H S% L%` triplet (see `accent`).
   */
  accentStrong: string;
  /** 11-stop scale, full `hsl(...)` form — used directly as a background value. */
  scale: readonly string[];
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) throw new Error(`Invalid hex: ${hex}`);
  const r = parseInt(m[1]!, 16) / 255;
  const g = parseInt(m[2]!, 16) / 255;
  const b = parseInt(m[3]!, 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

// Returns a raw "H S% L%" triplet (no `hsl(...)` wrapper) so it can be assigned
// to a CSS custom property that the Tailwind preset wraps with `hsl(... / <alpha-value>)`.
// Wrapping here would produce `hsl(hsl(...))` at the consumer and silently fail.
function hslTriplet(h: number, s: number, l: number): string {
  const round = (n: number) => Math.round(n * 100) / 100;
  return `${round(h)} ${round(s)}% ${round(l)}%`;
}

/**
 * Generate a brand palette from a single accent hex.
 * Snaps the accent's lightness to 50% to ensure 4.5:1 contrast on white.
 *
 * `accent`, `accentFg`, and `accentStrong` are raw `H S% L%` triplets meant
 * to be written into CSS variables consumed by `hsl(var(--token) / <alpha>)`.
 * `scale` keeps the full `hsl(...)` form because it's used directly as a
 * background value in the showcase.
 */
export function derivePalette(hex: string): BrandPalette {
  const { h, s } = hexToHsl(hex);
  const accent = hslTriplet(h, s, 50);
  const accentFg = hslTriplet(0, 0, 100);
  const accentStrong = hslTriplet(h, s, 38);
  const scale = deriveAccentScale(hex);
  return { accent, accentFg, accentStrong, scale };
}
